import { useMemo, useState } from 'react';
import { FORMULATION_LABELS } from '@/domain/workflow/formulation';
import { approachById } from '@/domain/workflow/approaches';
import { Link, useParams } from 'react-router-dom';
import type { AIImport, Client } from '@/domain/schema/v1';
import type { ImportPlan } from '@/domain/ai/plan';
import { continuationPrompt, repairPrompt, PROMPT_VERSIONS } from '@/domain/ai/prompts';
import { RISK_LABELS } from '@/domain/workflow/brief';
import { PROFILE_FIELDS } from '@/domain/workflow/profileMerge';
import { AiBridge, type FormulationChoice } from '@/services/aiBridge';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { useRequiredSession } from '../../state/session';
import { flushAll } from '../../state/saves';
import { CopyButton } from '../../components/CopyButton';
import { ReceiveBox, SendButton } from '../../components/ExternalRoundTrip';
import { ClaudeProjectPanel } from '../../components/ClaudeProjectPanel';

function useAiBridge() {
  const s = useRequiredSession();
  const svc = useService();
  return useMemo(() => new AiBridge(s.repo, s.ctx, svc), [s, svc]);
}

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/** AI 연결 (AI_WORKFLOW_PLAN_v1): 회기 자료 복사 → Claude 프로젝트(내담자별)에서 실행 → 결과 붙여넣기 → 미리보기 → 반영. 한 번 왕복 */
export function AiScreen() {
  const { sessionId = '' } = useParams();
  const svc = useService();
  const ai = useAiBridge();
  const {
    data,
    reload,
    error: loadError,
  } = useLoad(async () => {
    await flushAll();
    const session = await svc.getSession(sessionId);
    const [client, packet, imports] = await Promise.all([
      svc.getClient(session.clientId),
      ai.sessionPacket(sessionId),
      ai.listImports(sessionId),
    ]);
    return { session, client, packet, imports };
  }, [svc, ai, sessionId]);

  if (loadError) return <p role="alert">회기를 찾을 수 없습니다.</p>;
  if (!data) return <p className="muted">분석자료 준비 중…</p>;
  const { session, client, packet, imports } = data;
  const analyses = imports.filter((i) => i.purpose === 'session-analysis');
  const reviews = imports.filter((i) => i.purpose === 'second-review');

  return (
    <section className="stack">
      <div className="page-head">
        <div>
          <h1>
            AI 분석 · {client.clientCode} {session.sessionNumber}회기
          </h1>
          <p className="muted">AI 결과는 모두 제안으로만 들어갑니다. 승인·확정은 상담자가 합니다.</p>
        </div>
        <Link to={`/sessions/${sessionId}`}>회기로 돌아가기</Link>
      </div>

      <ClaudeProjectPanel client={client} />

      <DirectionBox client={client} onSaved={reload} />

      <div className="card stack">
        <h2>1. 외부로 보내기</h2>
        <p className="muted small">
          누르면 회기 자료가 복사되고 <strong>상담｜{client.clientCode}</strong> 프로젝트가 열립니다. 프로젝트 입력창에
          붙여넣고 보낸 뒤(회기마다 프로젝트 안에서 대화를 새로 시작), 답을 복사해서 이 화면으로 돌아오세요.
          실명·연락처·주소·생년월일은 들어가지 않습니다.
          {session.status === 'verification' && ' 아직 확인하지 않은 전사 줄은 "확인 필요"로 표시되어 전달됩니다.'}
        </p>
        <SendButton
          text={packet}
          projectUrl={client.externalProjectUrl}
          label="외부로 보내기 (회기 자료)"
          onSent={() => void ai.markPacketCopied(sessionId)}
        />
      </div>

      <ReceiveBox
        title="2. 외부 결과 가져오기"
        hint="외부 답 전체를 복사해 두면 돌아왔을 때 알아서 찾습니다. 앞뒤 설명이 섞여 있어도 됩니다."
        onSubmit={(raw) => ai.importSessionAnalysis(sessionId, raw).then(reload)}
      />

      {analyses.length > 0 && (
        <div className="stack">
          <h2>가져온 결과</h2>
          {[...analyses].reverse().map((imp, i) => (
            <ImportCard key={imp.id} imp={imp} ai={ai} latest={i === 0} onChanged={reload} />
          ))}
        </div>
      )}

      <SecondReview
        sessionId={sessionId}
        ai={ai}
        reviews={reviews}
        onChanged={reload}
        projectUrl={client.externalProjectUrl}
      />
    </section>
  );
}

export function ImportCard({
  imp,
  ai,
  latest,
  onChanged,
}: {
  imp: AIImport;
  ai: AiBridge;
  latest: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(latest && !imp.appliedAt);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [choices, setChoices] = useState<Record<number, FormulationChoice>>({});
  const { data: preview } = useLoad(
    async () => (open && imp.parseStatus === 'parsed' ? ai.previewImport(imp.id) : null),
    [ai, imp.id, imp.revision, open],
  );
  const status = imp.appliedAt
    ? '반영됨'
    : imp.parseStatus === 'failed'
      ? '읽기 실패'
      : imp.parseStatus === 'parsed'
        ? '반영 전'
        : '저장됨';

  return (
    <div className={`card stack ${imp.parseStatus === 'failed' ? 'warn-card' : ''}`}>
      <div className="row">
        <strong>{fmtTime(imp.createdAt)}</strong>
        <span className={`badge ${imp.appliedAt ? '' : 'badge-review'}`}>{status}</span>
        <span className="muted small">외부</span>
        {imp.parseStatus === 'parsed' && (
          <button type="button" onClick={() => setOpen((o) => !o)}>
            {open ? '접기' : '내용 보기'}
          </button>
        )}
      </div>

      {imp.parseStatus === 'failed' && (
        <>
          <p className="small">
            결과를 읽지 못했습니다 ({imp.parseErrors?.join(', ')}). 붙여넣은 원문은 저장되어 있습니다. 아래 버튼으로
            형식 복구 요청을 복사해 외부에 보내고, 받은 답을 다시 붙여넣으세요.
          </p>
          <CopyButton text={repairPrompt(imp.rawOutput, PROMPT_VERSIONS.sessionAnalysis)} label="형식 복구 요청 복사" />
        </>
      )}

      {open && preview && (
        <PlanPreview plan={preview.plan} warnings={preview.warnings} sessionId={imp.sessionId ?? ''} />
      )}
      {open && preview && !imp.appliedAt && (preview.plan.formulation?.unmatched.length ?? 0) > 0 && (
        <UnmatchedChoices
          items={preview.plan.formulation!.unmatched}
          options={preview.plan.formulation!.hypothesisOptions}
          choices={choices}
          onChange={setChoices}
        />
      )}
      {open && preview && preview.plan.missingSections.length > 0 && (
        <div className="warn-card card stack small">
          <span>응답이 중간에 끊겨 빠진 항목이 있습니다: {preview.plan.missingSections.join(', ')}</span>
          <CopyButton text={continuationPrompt(preview.plan.missingSections)} label="빠진 항목만 다시 요청 (복사)" />
          <span className="muted">
            같은 대화에 붙여넣고, 받은 결과를 위 칸에 다시 붙여넣으면 빠진 부분이 더해집니다.
          </span>
        </div>
      )}
      {open && imp.parseStatus === 'parsed' && !preview && <p className="muted small">미리보기 준비 중…</p>}

      {imp.parseStatus === 'parsed' && !imp.appliedAt && (
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setErr(null);
            void flushAll()
              .then(() => ai.applyImport(imp.id, { formulationChoices: choices }))
              .then((r) => {
                setResult(r.warnings);
                onChanged();
              })
              .catch((e: Error) => setErr(e.message))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? '반영 중…' : '제안으로 반영'}
        </button>
      )}
      {err && (
        <p role="alert" className="warn small">
          반영 중 문제가 생겼습니다: {err}. 다시 누르면 이어서 반영합니다 (중복되지 않음).
        </p>
      )}
      {imp.appliedAt && (
        <p className="muted small">
          회기 화면의 분석·근거·위험·목표, 사례개념화·진단적 고려 개정안, 상담기록지 초안에 제안으로 들어갔습니다. 각
          화면에서 검토·승인하세요.
        </p>
      )}
      {result && result.length > 0 && (
        <ul className="small">
          {result.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const KIND_KO = { strengthened: '강화', weakened: '약화', rejected: '폐기' } as const;

/** AI가 강화·약화·폐기라고 했지만 기존 가설과 자동으로 연결하지 못한 의견: 반영 전에 여기서 고른다 */
function UnmatchedChoices({
  items,
  options,
  choices,
  onChange,
}: {
  items: { kind: 'strengthened' | 'weakened' | 'rejected'; text: string }[];
  options: { id: string; text: string }[];
  choices: Record<number, FormulationChoice>;
  onChange: (c: Record<number, FormulationChoice>) => void;
}) {
  const value = (c: FormulationChoice | undefined) => (!c ? 'later' : c.action === 'link' ? `link:${c.id}` : c.action);
  const parse = (v: string): FormulationChoice =>
    v.startsWith('link:') ? { action: 'link', id: v.slice(5) } : ({ action: v } as FormulationChoice);
  return (
    <div className="warn-card card stack">
      <strong className="small">사례개념화 — 기존 가설과 자동으로 연결하지 못한 AI 의견 ({items.length})</strong>
      <p className="muted small">
        반영하기 전에 각각 어떻게 할지 고르세요. 그대로 두면 사례개념화 탭에서 나중에 정리할 수 있게 남겨 둡니다.
      </p>
      {items.map((u, i) => (
        <div key={i} className="issue small stack">
          <div>
            <span className="badge badge-review">{KIND_KO[u.kind]}</span> {u.text}
          </div>
          <select
            aria-label="이 의견 처리"
            value={value(choices[i])}
            onChange={(e) => onChange({ ...choices, [i]: parse(e.target.value) })}
          >
            <option value="later">나중에 사례개념화 탭에서 정리</option>
            {options.map((h) => (
              <option key={h.id} value={`link:${h.id}`}>
                기존 가설에 연결({KIND_KO[u.kind]}): {h.text.length > 50 ? `${h.text.slice(0, 50)}…` : h.text}
              </option>
            ))}
            {u.kind !== 'rejected' && <option value="new">새 가설로 추가</option>}
            {u.kind === 'strengthened' && <option value="protective">강점·자원(보호요인)으로 추가</option>}
            <option value="discard">버리기</option>
          </select>
        </div>
      ))}
    </div>
  );
}

const ROLE_KO: Record<string, string> = { counselor: '상담자', client: '내담자', unknown: '불명', mixed: '혼합' };

function PlanPreview({ plan, warnings, sessionId }: { plan: ImportPlan; warnings: string[]; sessionId: string }) {
  const reports = plan.evidence.filter((e) => e.evidenceType === 'CLIENT_REPORT');
  const unsure = reports.filter((e) => e.needsVerification).length;
  const riskKeys = Object.keys(plan.risk ?? {}) as (keyof typeof RISK_LABELS)[];
  const rows: [string, string | undefined][] = [
    ['회기 요약', plan.analysis.sessionSummary],
    [
      '근거',
      plan.evidence.length
        ? `내담자 진술 ${reports.length}개${unsure ? ` (그중 ${unsure}개는 화자·전사 확인 필요)` : ''}, 상담자 관찰 ${plan.evidence.filter((e) => e.evidenceType === 'COUNSELOR_OBSERVATION').length}개, 가설 ${plan.evidence.filter((e) => e.evidenceType === 'HYPOTHESIS').length}개`
        : undefined,
    ],
    [
      '위험 (승인 전)',
      riskKeys.length ? riskKeys.map((k) => `${RISK_LABELS[k] ?? k}: ${plan.risk![k]!.status}`).join(', ') : undefined,
    ],
    [
      '목표 진행',
      plan.goals.length
        ? plan.goals.map((g) => `${g.title}${g.to !== g.from ? ` → ${g.to}` : ''}`).join(', ')
        : undefined,
    ],
    [
      '사례개념화 개정안',
      plan.formulation
        ? [
            plan.formulation.addedTexts.length && `새 가설 ${plan.formulation.addedTexts.length}개`,
            Object.keys(plan.formulation.addedItems).length &&
              `칸 채움 ${Object.entries(plan.formulation.addedItems)
                .map(([k, v]) => `${FORMULATION_LABELS[k as keyof typeof FORMULATION_LABELS] ?? k} ${v.length}`)
                .join(', ')}`,
            plan.formulation.strengthenedIds.length && `강화 ${plan.formulation.strengthenedIds.length}`,
            plan.formulation.weakenedIds.length && `약화 ${plan.formulation.weakenedIds.length}`,
            plan.formulation.rejectedIds.length && `폐기 제안 ${plan.formulation.rejectedIds.length}`,
            plan.formulation.unmatched.length &&
              `기존 가설과 연결 못한 의견 ${plan.formulation.unmatched.length}개 → 반영 후 사례개념화 탭 'AI 의견 정리'에서 연결·추가·버리기`,
          ]
            .filter(Boolean)
            .join(', ') || '변경 이유만'
        : undefined,
    ],
    [
      '진단적 고려 개정안',
      plan.diagnosis?.considerations.length
        ? plan.diagnosis.considerations.map((c) => c.diagnosisName).join(', ')
        : undefined,
    ],
    [
      '상담기록지 초안',
      plan.journal
        ? `${Object.keys(plan.journal.fields).length}칸 채움${plan.journal.skipped.length ? ` (직접 쓴 ${plan.journal.skipped.length}칸은 그대로 둠)` : ''}`
        : undefined,
    ],
    [
      '변화 지점 (축어록·대화록 구간)',
      plan.changePoints.length
        ? plan.changePoints
            .map(
              (c, k) =>
                `${k + 1}) ${c.type}${c.theory ? ` (${c.theory})` : ''}${c.whatChanged ? ` — ${c.whatChanged}` : ''}`,
            )
            .join(' / ')
        : undefined,
    ],
    [
      '대화록 초안',
      Object.keys(plan.dialogueDraft).length && plan.verbatims.length
        ? '첫 번째 변화 지점 구간으로 자동 생성 (문서에서 확인·수정)'
        : undefined,
    ],
    [
      '상담 방향 계획',
      plan.directionPlan
        ? `${plan.directionPlan.summary ?? ''}${plan.directionPlan.steps.length ? ` — ${plan.directionPlan.steps.map((x) => `${x.technique}${x.theory ? `(${x.theory})` : ''}`).join(', ')}` : ''}`
        : undefined,
    ],
    [
      '자기 점검',
      plan.selfReview
        ? `근거 약한 추론 ${plan.selfReview.unsupportedInference.length}, 대안가설 ${plan.selfReview.alternativeHypotheses.length}, 수퍼비전 질문 ${plan.selfReview.supervisionQuestions.length} → 수퍼비전 노트로`
        : undefined,
    ],
    [
      '치료 접근 근거',
      plan.approachEvidence.length
        ? `${plan.approachEvidence.length}개 — ${approachCounts(plan.approachEvidence)} (치료 방향 탭에 쌓임)`
        : undefined,
    ],
    [
      '화자 고침',
      plan.roleReviews.length
        ? `${plan.roleReviews.length}줄 — 반영하면 바뀝니다 (아래 목록, '추정'으로 표시)`
        : undefined,
    ],
    [
      '내담자 정보에 덧붙일 내용',
      plan.profile.some((p) => p.ok)
        ? plan.profile
            .filter((p) => p.ok)
            .map((p) => `${PROFILE_FIELDS[p.field].label}: ${p.changed ? '변경 → ' : ''}${p.value}`)
            .join(' / ')
        : undefined,
    ],
    [
      '내담자 정보 — 반영 안 함 (확인 필요)',
      plan.profile.some((p) => !p.ok)
        ? plan.profile
            .filter((p) => !p.ok)
            .map((p) => `${PROFILE_FIELDS[p.field].label}: ${p.value} (${p.reason})`)
            .join(' / ')
        : undefined,
    ],
    ['축어록 추천', plan.verbatims.length ? `${plan.verbatims.length}구간` : undefined],
  ];
  return (
    <div className="stack">
      <dl className="kv">
        {rows
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
      </dl>
      {plan.roleReviews.length > 0 && (
        <details className="card">
          <summary className="small">
            AI가 고칠 화자 {plan.roleReviews.length}줄 (반영하면 바뀝니다. 틀렸으면 전사에서 다시 고치세요)
          </summary>
          <ul className="small">
            {plan.roleReviews.map((r) => (
              <li key={r.segmentId}>
                [{r.segmentId}] {ROLE_KO[r.from] ?? r.from} → <strong>{ROLE_KO[r.role]}</strong> · {r.text}
                {r.reason && <span className="muted"> ({r.reason})</span>}{' '}
                <Link to={`/sessions/${sessionId}?seg=${encodeURIComponent(r.segmentId)}`}>전사에서 보기</Link>
              </li>
            ))}
          </ul>
        </details>
      )}
      {plan.questions.length > 0 && (
        <div>
          <strong className="small">AI가 상담자에게 묻는 것</strong>
          <ul className="small">
            {plan.questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
          <p className="muted small">
            답하려면 외부 AI의 같은 대화에 답을 쓰세요. 외부에서 고친 결과를 위 칸에 다시 붙여넣고 반영하면 이 회기
            내용이 갱신됩니다 (중복되지 않고, 직접 확인·수정하신 내용은 그대로).
          </p>
        </div>
      )}
      {plan.issues.length > 0 && (
        <div className="warn-card card stack">
          <strong className="small">확인이 필요한 근거 ({plan.issues.length})</strong>
          {plan.issues.map((it, k) => (
            <div key={k} className="issue small">
              <div className="muted">{it.message}</div>
              <div>
                AI가 쓴 진술: <strong>“{it.text}”</strong>
              </div>
              {it.lines?.map((l) => (
                <div key={l.segmentId} className="issue-line">
                  [{l.segmentId} · {l.role === 'counselor' ? '상담자' : l.role === 'client' ? '내담자' : '불명'}
                  {l.sure ? '' : '(추정)'}] {l.text}{' '}
                  <Link to={`/sessions/${sessionId}?seg=${encodeURIComponent(l.segmentId)}`}>
                    전사에서 보기·화자 고치기
                  </Link>
                </div>
              ))}
              {!it.lines?.length && it.segmentIds[0] && (
                <Link to={`/sessions/${sessionId}?seg=${encodeURIComponent(it.segmentIds[0])}`}>전사에서 보기</Link>
              )}
            </div>
          ))}
          <p className="muted small">
            실제로 내담자가 한 말이면: 전사에서 그 줄의 화자를 "내담자 발언"으로 고친 뒤, 회기 화면 &gt; 임상 근거에서
            "내담자 진술 맞음"을 누르세요. 상담자 말이면 그대로 두면 됩니다 (내담자 진술로 쓰이지 않음).
          </p>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="warn-card card">
          <strong className="small">확인할 점</strong>
          <ul className="small">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function SecondReview({
  sessionId,
  ai,
  reviews,
  onChanged,
  projectUrl,
}: {
  sessionId: string;
  ai: AiBridge;
  reviews: AIImport[];
  onChanged: () => void;
  projectUrl?: string | undefined;
}) {
  const [questions, setQuestions] = useState('');
  const [packet, setPacket] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { data: parsed } = useLoad(() => ai.secondReviews(sessionId), [ai, sessionId, reviews.length]);
  return (
    <details className="card">
      <summary>
        <strong>별도 2차 검토 (선택)</strong>{' '}
        <span className="muted small">자기 점검은 이제 회기 결과에 함께 들어옵니다</span>
      </summary>
      <div className="stack">
        <p className="muted small">
          1차 분석을 반영한 뒤, 근거 부족·대안가설·편향을 독립적으로 검토받습니다. 전사 전체는 보내지 않습니다.
        </p>
        <textarea
          aria-label="검토받고 싶은 질문"
          rows={2}
          value={questions}
          placeholder="검토받고 싶은 질문 (선택)"
          onChange={(e) => {
            setQuestions(e.target.value);
            setPacket(null);
          }}
        />
        {packet === null ? (
          <button
            type="button"
            onClick={() => {
              setErr(null);
              void ai
                .secondReviewPacket(sessionId, questions)
                .then((p) => (p ? setPacket(p) : setErr('먼저 1차 분석을 반영하거나 회기 분석을 작성하세요.')));
            }}
          >
            2차 검토자료 만들기
          </button>
        ) : (
          <SendButton text={packet} projectUrl={projectUrl} label="외부로 보내기 (검토자료)" />
        )}
        {err && <p className="warn small">{err}</p>}
        <ReceiveBox
          title="검토 결과 가져오기"
          hint="결과는 수퍼비전 노트로 저장됩니다."
          onSubmit={(raw) =>
            ai.importSecondReview(sessionId, raw).then((r) => {
              onChanged();
              if (!r.ok) throw new Error(`읽지 못했습니다 (${r.errors?.join(', ')}). 원문은 저장되었습니다.`);
            })
          }
        />
        {reviews.some((r) => r.parseStatus === 'failed') && (
          <CopyButton
            text={repairPrompt(
              reviews.filter((r) => r.parseStatus === 'failed').at(-1)!.rawOutput,
              PROMPT_VERSIONS.secondReview,
            )}
            label="마지막 실패한 결과의 형식 복구 요청 복사"
          />
        )}
        {parsed?.map((r, i) => (
          <dl className="kv" key={i}>
            {(
              [
                ['근거 부족한 추론', r.unsupportedInference],
                ['대안가설', r.alternativeHypotheses],
                ['추가 확인할 정보', r.missingInformation],
                ['반증 자료', r.contradictoryEvidence],
                ['이론 편향', r.theoryBias],
                ['개입 우려', r.interventionConcerns],
                ['상담자 과정 질문', r.counselorProcessQuestions],
                ['수퍼비전 질문', r.supervisionQuestions],
              ] as const
            )
              .filter(([, v]) => v.length)
              .map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>
                    {v.map((x, j) => (
                      <div key={j}>· {x}</div>
                    ))}
                  </dd>
                </div>
              ))}
          </dl>
        ))}
      </div>
    </details>
  );
}

/** 상담자가 이끌어 가고자 하는 방향: 내담자마다 저장, 회기 자료에 함께 들어감 */
export function DirectionBox({
  client,
  onSaved,
  collapsed,
}: {
  client: Client;
  onSaved: () => void;
  collapsed?: boolean;
}) {
  const svc = useService();
  const [text, setText] = useState(client.counselingDirection ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const dirty = text.trim() !== (client.counselingDirection ?? '').trim();
  return (
    <details className="card" open={!collapsed && !client.counselingDirection}>
      <summary>
        <strong>상담 방향</strong>{' '}
        <span className="muted small">
          {client.counselingDirection
            ? client.counselingDirection.slice(0, 40) + (client.counselingDirection.length > 40 ? '…' : '')
            : '(선택) 이끌어 가고 싶은 방향'}
        </span>
      </summary>
      <div className="stack">
        <textarea
          aria-label="상담 방향"
          rows={3}
          value={text}
          maxLength={2000}
          placeholder="예: 자기비판을 자비중심 접근으로 다루고, 진로 결정은 내담자가 스스로 하도록 돕고 싶다"
          onChange={(e) => {
            setText(e.target.value);
            setState('idle');
          }}
        />
        <div className="row">
          <button
            type="button"
            disabled={!dirty || state === 'saving'}
            onClick={() => {
              setState('saving');
              void svc.updateClient(client.id, { counselingDirection: text.trim() || undefined }).then(() => {
                setState('saved');
                onSaved();
              });
            }}
          >
            {state === 'saving' ? '저장 중…' : '방향 저장'}
          </button>
          {state === 'saved' && <span className="muted small">저장됨 · 회기 자료에 반영됨</span>}
        </div>
        <p className="muted small">
          AI가 이 방향에 맞춘 진행 계획(기법·이론·방법·말 예시)을 함께 제시합니다. 사례개념화와 맞지 않는 부분은 근거와
          함께 알려줍니다.
        </p>
      </div>
    </details>
  );
}

/** 접근별 근거 수: "인지행동치료 4, 정서중심치료 2" */
function approachCounts(items: { approach: string }[]): string {
  const n = new Map<string, number>();
  for (const x of items) n.set(x.approach, (n.get(x.approach) ?? 0) + 1);
  return [...n]
    .sort((a, b) => b[1] - a[1])
    .map(([id, k]) => `${approachById(id)?.name ?? id} ${k}`)
    .join(', ');
}
